import React, { useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useLocation } from "react-router-dom";
import { loadStoryRoute } from "../utils/storyRoute";
import { getStoryRouteLocal, loadStoryRouteLocal } from "../utils/storyRouteLocal";
import { isReviewModeEnabled } from "../utils/reviewMode";
import { reportAppError, reportPerformance } from "../utils/observability";
import WindroosSpinner from "../components/WindroosSpinner";
import ContentReviewChip from "../components/ContentReviewChip";
import HeroSection from "./storyRoute/HeroSection";
import RouteStepsSection from "./storyRoute/RouteStepsSection";
import TextCardsSection from "./storyRoute/TextCardsSection";
import FeatureSplitSection from "./storyRoute/FeatureSplitSection";
import CompactCardsSection from "./storyRoute/CompactCardsSection";
import NewsGridSection from "./storyRoute/NewsGridSection";
import CtaBandSection from "./storyRoute/CtaBandSection";
import SideRail from "./storyRoute/SideRail";
import VacancyBanner from "./storyRoute/VacancyBanner";
import { COLORS } from "../theme/tokens";

const SECTION_RENDERERS = {
  hero: HeroSection,
  "route-steps": RouteStepsSection,
  "text-cards": TextCardsSection,
  "feature-split": FeatureSplitSection,
  "compact-cards": CompactCardsSection,
  "news-grid": NewsGridSection,
  "cta-band": CtaBandSection,
};

const CMS_LOAD_TIMEOUT_MS = 6000;
const reportedUnsupportedSections = new Set();

async function loadStoryRouteWithFallback() {
  const cmsPromise = loadStoryRoute("story-route");
  cmsPromise.catch(() => {});

  try {
    const cmsData = await Promise.race([
      cmsPromise,
      new Promise((_, reject) =>
        window.setTimeout(
          () => reject(new Error("Story route CMS load timed out")),
          CMS_LOAD_TIMEOUT_MS
        )
      ),
    ]);

    if (cmsData.length > 0) {
      return cmsData;
    }
  } catch (error) {
    reportAppError({ stage: "cms_load", error });
    // Fall back to the bundled seed so the public UAT route never renders blank.
  }

  return loadStoryRouteLocal();
}

function SectionRenderer({ section }) {
  const Component = SECTION_RENDERERS[section.contentType];
  if (!Component) {
    if (!reportedUnsupportedSections.has(section.id)) {
      reportedUnsupportedSections.add(section.id);
      reportAppError({ stage: "unsupported_section", errorCode: "unsupported_type" });
    }
    if (!isReviewModeEnabled()) return null;
    return (
      <Container sx={{ py: 4 }}>
        <ContentReviewChip status="mist" label="Niet-ondersteunde sectie" />
      </Container>
    );
  }
  return <Component section={section} />;
}

function LocalPreviewBanner() {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 1100,
        backgroundColor: "#FFF4CE",
        borderBottom: "1px solid #E0C870",
        px: 2,
        py: 1,
        textAlign: "center",
        fontSize: 13,
        color: "#5A4500",
      }}
    >
      Lokale preview — geladen uit{" "}
      <code>seed/story-route-seed.json</code>. Geen AppSync, geen S3 schrijfacties.
    </Box>
  );
}

function LoadingState({ isLocal, message }) {
  return (
    <>
      {isLocal && <LocalPreviewBanner />}
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          gap: 2,
          py: 10,
          px: 3,
          minHeight: { xs: "calc(100vh - 100px)", md: "calc(100vh - 154px)" },
          textAlign: "center",
        }}
      >
        <WindroosSpinner label={message || "De Windroos laadt"} size={56} />
      </Box>
    </>
  );
}

function StoryRoute() {
  const { hash, search } = useLocation();

  const source = useMemo(() => {
    const params = new URLSearchParams(search);
    return params.get("source") === "local" ? "local" : "cms";
  }, [search]);
  const localHero = useMemo(
    () =>
      source === "cms"
        ? getStoryRouteLocal().find((section) => section.contentType === "hero")
        : null,
    [source]
  );
  const [sections, setSections] = useState(() => (localHero ? [localHero] : []));
  const [status, setStatus] = useState({
    state: localHero ? "preview" : "loading",
    message: "",
  });
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (source === "cms" && localHero) {
      setSections([localHero]);
      setStatus({ state: "preview", message: "" });
    } else {
      setStatus({ state: "loading", message: "" });
    }

    const loader =
      source === "local"
        ? loadStoryRouteLocal
        : loadStoryRouteWithFallback;
    const startedAt = performance.now();
    const finalData = loader();

    finalData
      .then((data) => {
        reportPerformance({ stage: "cms_load", durationMs: performance.now() - startedAt });
        if (cancelled) return;
        setSections(data);
        setStatus({ state: data.length === 0 ? "empty" : "ready", message: "" });
      })
      .catch((error) => {
        reportAppError({
          stage: "cms_load",
          error,
          durationMs: performance.now() - startedAt,
        });
        if (cancelled) return;
        setStatus({ state: "error", message: error.message || String(error) });
      });

    return () => {
      cancelled = true;
    };
  }, [source, localHero, retryCount]);

  useEffect(() => {
    if (status.state !== "ready" || !hash) return undefined;

    const sectionId = decodeURIComponent(hash.slice(1));
    const timeout = window.setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ block: "start" });
    }, 0);

    return () => window.clearTimeout(timeout);
  }, [hash, sections, status.state]);

  const isLocal = source === "local";
  const displayedSections = useMemo(() => {
    const conversion = sections.find(
      (section) => section.sectionName === "contact-aanmelden"
    );
    if (!conversion) return sections;

    const journey = sections.filter((section) => section !== conversion);
    const schoolIndex = journey.findIndex(
      (section) => section.sectionName === "school"
    );
    if (schoolIndex < 0) return sections;

    journey.splice(schoolIndex + 1, 0, conversion);
    return journey;
  }, [sections]);
  const vacancyBlock = useMemo(
    () =>
      sections
        .flatMap((section) => section.blocks || [])
        .find((block) => block.blockType === "vacancy-highlight"),
    [sections]
  );

  if (status.state === "loading") {
    return <LoadingState isLocal={isLocal} message={status.message} />;
  }

  if (status.state === "error") {
    return (
      <>
        {isLocal && <LocalPreviewBanner />}
        <Container sx={{ py: 6 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontFamily: "windroos, Georgia, serif", color: COLORS.blue, mb: 1 }}
          >
            Deze pagina kon niet volledig worden geladen
          </Typography>
          <Typography sx={{ color: "#203033", lineHeight: 1.65, mb: 2 }}>
            Probeer het opnieuw. Lukt het daarna nog niet? Neem dan contact met
            ons op.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Button
              onClick={() => setRetryCount((count) => count + 1)}
              sx={{ textTransform: "none", fontWeight: 800 }}
            >
              Probeer opnieuw
            </Button>
            <Button href="/contact" sx={{ textTransform: "none", fontWeight: 800 }}>
              Neem contact op
            </Button>
          </Stack>
        </Container>
      </>
    );
  }

  if (status.state === "empty") {
    return (
      <>
        {isLocal && <LocalPreviewBanner />}
        <Container sx={{ py: 8 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            {isLocal ? (
              <>
                De lokale preview bevat nog geen actieve onderdelen.
              </>
            ) : (
              <>
                De verhaalroute is nog niet gevuld. Zet deze pagina pas open
                voor testers wanneer de inhoud zichtbaar is.
              </>
            )}
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Tot die tijd toont deze pagina geen secties.
          </Typography>
        </Container>
      </>
    );
  }

  return (
    <Box component="main">
      {isLocal && <LocalPreviewBanner />}
      {status.state !== "preview" && <SideRail />}
      <VacancyBanner block={vacancyBlock} />
      {displayedSections.map((section) => (
        <SectionRenderer key={section.sectionName} section={section} />
      ))}
    </Box>
  );
}

export default StoryRoute;
